import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Configuration, EntityKeys } from '../models/configuration.model';
import { configurationData, configurationSchema } from './configuration-data';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  private _injector = inject(Injector);
  public clientName: EntityKeys;
  public configuration: Configuration;
  private _document = inject(DOCUMENT);
  private _domDocument = document;
  public provenueEnvironment: string;

  public init = () => {
    const client: string = this.getClientFromHostname();
    this.setProvenueEnvironment();
    let configuration = configurationData[client];
    const { success } = configurationSchema.safeParse(configuration);
    const isLocalEnvironment = location.href.includes('local');

    if (!success || client === 'default') {
      this.configuration = this.fallbackToDefaultConfig();
      if (!isLocalEnvironment) {
        this.navigateToNotFound();
        return;
      }
    }

    configuration = configuration ?? this.configuration;

    this._document
      .getElementById('appFavicon')
      .setAttribute('href', configuration.favicon);
    this.fillWithEnvConfiguration(configuration);
    this.declareGlobalCssVariables();
  };

  public getPortalBaseUrl(): string {
    const client = this.clientName || this.getClientFromHostname();
    const environment = this.getEnvironmentPrefixFromHostname();

    if (environment === 'local') {
      return `http://local.${client}.3ddigitalvenue.com:4200`;
    }

    const environmentPrefix = environment ? `${environment}-` : '';

    return `https://${environmentPrefix}${client}.groupsales.3ddigitalvenue.com`;
  }

  private getEnvironmentPrefixFromHostname(): string {
    const hostname = location.hostname;

    if (hostname.startsWith('local.')) {
      return 'local';
    }

    const match = hostname.match(/(?:^|\.|-)(dev|pre)-/);
    if (match) {
      return match[1];
    }

    return '';
  }

  private getClientFromHostname() {
    const regex: RegExp =
      /^(?:http|https):\/\/(?:local\.)?(?:private\.)?(?:private\-)?(?:demo-)?(?:dev-)?(?:pre-)?(?:uat-)?(.*)?(?:\.groupsales)?\.3ddigitalvenue\.com.*/gm;
    const matches = regex.exec(location.href);

    const client: any = matches?.[1]?.split('.')?.[0] as EntityKeys;
    const isLocalEnvironment = false;

    if (
      // @ts-ignore
      (!isLocalEnvironment && client === 'default') ||
      typeof configurationData[client] == 'undefined'
    ) {
      this.configuration = this.fallbackToDefaultConfig();
      this.navigateToNotFound();
    } else {
      this.clientName = client;
    }
    return client;
  }

  private setProvenueEnvironment() {
    if (!this.provenueEnvironment) {
      const url = new URL(window.location.href);
      this.provenueEnvironment = url.searchParams.get('load-pv-env') ?? '';
    }
  }

  private fillWithEnvConfiguration(configuration: Configuration) {
    this.configuration = {} as Configuration;
    for (const [key, value] of Object.entries(configuration)) {
      this.configuration[key as keyof Configuration] = value;
    }
  }

  private declareGlobalCssVariables() {
    const root: HTMLElement | null = this._domDocument.querySelector(':root');

    if (root) {
      const theme = this.configuration['theme'];
      for (const [key, value] of Object.entries(theme)) {
        root.style.setProperty('--' + key, value);
      }

      const sdcBoTheme = this.configuration['sdcBoTheme'];
      for (const [token, value] of Object.entries(sdcBoTheme ?? {})) {
        root.style.setProperty('--' + token, value);
      }
    }
  }

  private fallbackToDefaultConfig() {
    this.clientName = 'default';
    return configurationData['default'];
  }

  private navigateToNotFound() {
    const router = this._injector.get(Router);
    router.navigate(['not-found']);
  }
}
